import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  Target,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Eye,
  Archive,
} from 'lucide-react';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';

async function fetchSubmissionMe(challengeId) {
  try {
    const res = await api.get(`/challenges/${challengeId}/submissions/me`);
    if (res.data?.success && res.data.data) return res.data.data;
  } catch {
    return null;
  }
  return null;
}

const StudentChallenges = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [listLoading, setListLoading] = useState(true);
  const [submissionById, setSubmissionById] = useState({});

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await api.get('/challenges/my/statistics');
      if (res.data?.success && res.data.data) setStats(res.data.data);
    } catch (e) {
      console.error(e);
      toast.error(t('studentChallenges.statsError'));
    } finally {
      setStatsLoading(false);
    }
  }, [t]);

  const loadList = useCallback(
    async (page = 1) => {
      setListLoading(true);
      try {
        const res = await api.get(`/challenges/my/available?page=${page}&limit=10`);
        if (res.data?.success && res.data.data) {
          const { challenges: list, pagination: pag } = res.data.data;
          setChallenges(list || []);
          setPagination({
            page: pag?.page ?? 1,
            limit: pag?.limit ?? 10,
            total: pag?.total ?? 0,
            pages: pag?.pages ?? 1,
          });
          const ids = (list || []).map((c) => c.id);
          const results = await Promise.all(ids.map((id) => fetchSubmissionMe(id)));
          const map = {};
          ids.forEach((id, i) => {
            const data = results[i];
            if (data?.submission) map[id] = data.submission;
          });
          setSubmissionById(map);
        }
      } catch (e) {
        console.error(e);
        toast.error(t('studentChallenges.listError'));
      } finally {
        setListLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadList(1);
  }, [loadList]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const sub = stats?.submissions || { total: 0, correct: 0, incorrect: 0 };
  const archived = stats?.archivedParticipations ?? 0;

  const statCards = [
    {
      key: 'total',
      value: sub.total,
      label: t('studentChallenges.statTotalSubmissions'),
      gradient: 'from-violet-500 to-indigo-600',
      bg: 'from-violet-50 to-indigo-50/80',
    },
    {
      key: 'correct',
      value: sub.correct,
      label: t('studentChallenges.statCorrect'),
      gradient: 'from-emerald-500 to-teal-600',
      bg: 'from-emerald-50 to-teal-50/80',
    },
    {
      key: 'incorrect',
      value: sub.incorrect,
      label: t('studentChallenges.statIncorrect'),
      gradient: 'from-rose-500 to-orange-600',
      bg: 'from-rose-50 to-orange-50/80',
    },
    {
      key: 'archived',
      value: archived,
      label: t('studentChallenges.statArchived'),
      gradient: 'from-slate-600 to-slate-800',
      bg: 'from-slate-50 to-slate-100/90',
    },
  ];

  const page = pagination.page;
  const totalPages = Math.max(1, pagination.pages);

  return (
    <div
      className="relative mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <DailyExercisesHero
        title={t('studentChallenges.title')}
        description={t('studentChallenges.subtitle')}
        icon={Target}
        iconClassName="text-fuchsia-600"
      />

      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {statsLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-28 min-w-0 animate-pulse rounded-[1.35rem] border-[3px] border-slate-200/80 bg-slate-100/90"
            />
          ))
        ) : (
          statCards.map((c) => {
            const cardInner = (
              <>
                <div
                  className={`absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-br ${c.gradient} opacity-15 blur-2xl`}
                  aria-hidden
                />
                <p
                  className={`break-words text-2xl font-black tabular-nums bg-gradient-to-r ${c.gradient} bg-clip-text text-transparent`}
                >
                  {c.value}
                </p>
                <p className="mt-1 break-words text-[11px] font-extrabold uppercase leading-snug tracking-wide text-slate-600 sm:text-xs">
                  {c.label}
                </p>
              </>
            );
            const baseCls = `relative flex min-h-[6.5rem] min-w-0 flex-col items-start overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/8 bg-gradient-to-br ${c.bg} p-4 shadow-[0_6px_0_0_rgba(15,23,42,0.06)]`;
            if (c.key === 'archived') {
              return (
                <Link
                  key={c.key}
                  to="/student/challenges/archive"
                  className={`${baseCls} block cursor-pointer transition-shadow hover:shadow-[0_8px_0_0_rgba(139,92,246,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2`}
                  aria-label={t('studentChallenges.archiveOpenAria')}
                >
                  {cardInner}
                </Link>
              );
            }
            return (
              <div key={c.key} className={baseCls}>
                {cardInner}
              </div>
            );
          })
        )}
      </div>

      {listLoading ? (
        <div className="rounded-[1.35rem] border-[3px] border-fuchsia-200/80 bg-gradient-to-br from-white to-fuchsia-50/80 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(217,70,239,0.1)]">
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-fuchsia-600" aria-hidden strokeWidth={2.25} />
          <p className="mt-4 text-sm font-bold text-slate-600 sm:text-base">{t('studentChallenges.loading')}</p>
        </div>
      ) : challenges.length === 0 ? (
        <div className="rounded-[1.35rem] border-[3px] border-slate-200/90 bg-gradient-to-br from-white to-violet-50/40 px-6 py-14 text-center shadow-[0_8px_0_0_rgba(139,92,246,0.08)]">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[1.25rem] border-[3px] border-fuchsia-200 bg-fuchsia-50">
            <Target className="h-11 w-11 text-fuchsia-500" strokeWidth={2} aria-hidden />
          </div>
          <p className="text-lg font-black text-slate-900 sm:text-xl">{t('studentChallenges.empty')}</p>
          <p className="mx-auto mt-2 max-w-md text-sm font-semibold leading-relaxed text-slate-600 sm:text-base">
            {t('studentChallenges.emptyHint')}
          </p>
        </div>
      ) : (
        <>
          <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
            <div className="border-b border-slate-200/90 bg-gradient-to-r from-fuchsia-50/90 via-white to-violet-50/50 px-4 py-3 sm:px-5">
              <h2 className="text-base font-black tracking-tight text-slate-900 sm:text-lg">
                {t('studentChallenges.listHeading')}
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b-2 border-slate-200/90 bg-slate-50/80">
                    <th
                      className={`px-3 py-3.5 text-[11px] font-extrabold uppercase tracking-wide text-slate-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    >
                      {t('studentChallenges.colName')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('studentChallenges.colPeriod')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('studentChallenges.colStatus')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('studentChallenges.colResult')}
                    </th>
                    <th className="whitespace-nowrap px-3 py-3.5 text-center text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      {t('studentChallenges.colAction')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {challenges.map((ch) => {
                    const submission = submissionById[ch.id];
                    const hasSubmission = !!submission;
                    return (
                      <tr
                        key={ch.id}
                        className="border-b border-slate-100 transition-colors hover:bg-sky-50/50"
                      >
                        <td
                          className={`min-w-0 max-w-[14rem] break-words px-3 py-3.5 font-semibold text-slate-900 sm:max-w-none ${isRTL ? 'text-right' : 'text-left'}`}
                        >
                          {ch.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-3.5 text-center text-xs text-slate-600">
                          {formatDate(ch.startsAt)} → {formatDate(ch.endsAt)}
                        </td>
                        <td className="px-3 py-3.5 text-center">
                          <span className="inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-xs font-bold capitalize text-violet-900">
                            {ch.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-3.5 text-center">
                          {!hasSubmission ? (
                            <span className="text-xs text-slate-400">—</span>
                          ) : submission.isCorrect ? (
                            <span className="inline-flex items-center gap-1 font-bold text-emerald-700">
                              <CheckCircle2 className="h-5 w-5 shrink-0" aria-hidden />
                              {t('studentChallenges.correct')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 font-bold text-rose-700">
                              <XCircle className="h-5 w-5 shrink-0" aria-hidden />
                              {t('studentChallenges.incorrect')}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3.5 text-center">
                          {!hasSubmission ? (
                            <Link
                              to={`/student/challenges/${ch.id}`}
                              className="inline-flex items-center gap-1.5 rounded-xl border-2 border-violet-300 bg-violet-50 px-3 py-1.5 text-xs font-bold text-violet-900 transition-colors hover:bg-violet-100"
                            >
                              <Eye className="h-4 w-4 shrink-0" aria-hidden />
                              {t('studentChallenges.btnView')}
                            </Link>
                          ) : (
                            <Link
                              to={`/student/challenges/${ch.id}`}
                              className="inline-flex items-center gap-1.5 rounded-xl border-2 border-slate-400 bg-slate-50 px-3 py-1.5 text-xs font-bold text-slate-900 transition-colors hover:bg-slate-100"
                            >
                              <Archive className="h-4 w-4 shrink-0" aria-hidden />
                              {t('studentChallenges.btnArchive')}
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {totalPages > 1 && (
            <nav
              className="flex flex-col gap-3 rounded-[1.35rem] border-[3px] border-slate-200/90 bg-slate-50/50 px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-4 sm:px-6"
              aria-label={t('studentChallenges.paginationAria')}
            >
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => loadList(page - 1)}
                className="inline-flex min-h-[2.5rem] items-center justify-center gap-1 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {isRTL ? <ChevronRight className="h-4 w-4 shrink-0" /> : <ChevronLeft className="h-4 w-4 shrink-0" />}
                {t('common.previous')}
              </button>
              <span className="text-center text-sm font-bold tabular-nums text-slate-600 sm:min-w-[12rem]">
                {t('studentChallenges.pageOf', {
                  page: pagination.page,
                  pages: pagination.pages,
                  total: pagination.total,
                })}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => loadList(page + 1)}
                className="inline-flex min-h-[2.5rem] items-center justify-center gap-1 rounded-xl border-2 border-slate-200 bg-white px-4 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t('common.next')}
                {isRTL ? <ChevronLeft className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              </button>
            </nav>
          )}
        </>
      )}
    </div>
  );
};

export default StudentChallenges;
